import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {api} from "../api";
import {clearFormDraft, readFormDraft, writeFormDraft} from "../formDrafts";
import DataTable from "./DataTable";
import type {IMenuAction} from "./ActionMenu";
import useDialogFocus from "./useDialogFocus";

interface IIdentityUsersPanelProps {
  rows: Array<Record<string, any>>;
  canUpdate: boolean;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  onStatusChanged: (userId: string, status: string, response: Record<string, any>) => void;
  userTimeZone: string;
  totalCount?: number;
  serverFilter: {field: string; value: string; onChange: (next: {field: string; value: string}) => void};
}

type PendingAction = {record: Record<string, any>; blocked: boolean};

const userIdOf = (record: Record<string, any>) => String(record._id || record.id || record.userId || "");
const statusOf = (record: Record<string, any>) => String(record.status || (
  record.datetimeDelete ? "deleted" : record.disabled ? "blocked" : record.datetimeVerified ? "active" : "inactive"
));
const draftKey = (pending: PendingAction) => `admin-id-express-form-draft:identity-user-status:${userIdOf(pending.record)}:${pending.blocked ? "block" : "unblock"}`;

export default function IdentityUsersPanel({rows, canUpdate, statusFilter, onStatusFilterChange, onStatusChanged, userTimeZone, totalCount, serverFilter}: IIdentityUsersPanelProps) {
  const {t} = useTranslation();
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<{message: string; email: string; userId: string; status: string} | null>(null);
  const normalizedRows = useMemo(() => rows.map(record => record.status
    ? record
    : {...record, status: statusOf(record)}), [rows]);

  const openAction = (record: Record<string, any>) => {
    const next = {record, blocked: statusOf(record) !== "blocked"};
    setPending(next);
    setReason(readFormDraft(draftKey(next), {reason: ""}).reason || "");
    setError("");
    setNotice(null);
  };

  useEffect(() => {
    if (!pending) return;
    writeFormDraft(draftKey(pending), {reason});
  }, [pending, reason]);

  useEffect(() => {
    if (!pending || !reason.trim()) return;
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [pending, reason]);

  const close = () => {
    if (busy) return;
    if (reason.trim() && !window.confirm(t("forms.closeDirtyConfirm"))) return;
    setPending(null);
    setReason("");
    setError("");
  };
  const dialogRef = useDialogFocus<HTMLDivElement>(Boolean(pending), close);

  const submit = async () => {
    if (!pending || !reason.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const userId = userIdOf(pending.record);
      const response = await api.put("/data/identity/users", {userId, blocked: pending.blocked, reason: reason.trim()});
      clearFormDraft(draftKey(pending));
      const resultingStatus = String(response.data?.status || (
        pending.blocked ? "blocked" : pending.record.datetimeVerified ? "active" : "inactive"
      ));
      onStatusChanged(userId, resultingStatus, response.data || {});
      setNotice({
        message: String(response.data?.message || t(pending.blocked ? "identityUsers.blockSuccess" : "identityUsers.unblockSuccess")),
        email: String(response.data?.user?.email || response.data?.email || pending.record.email || ""),
        userId: String(response.data?.user?.userId || response.data?.userId || userId),
        status: resultingStatus,
      });
      setPending(null);
      setReason("");
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || t("identityUsers.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  const actions = useMemo(() => (record: Record<string, any>): IMenuAction[] => {
    if (!canUpdate || statusOf(record) === "deleted") return [];
    const blocked = statusOf(record) === "blocked";
    return [{id: blocked ? "unblock" : "block", label: t(blocked ? "identityUsers.unblock" : "identityUsers.block"), isDanger: !blocked, icon: blocked ? "lock-open" : "lock"}];
  }, [canUpdate, t]);

  return <div className="identity-users-panel">
    {notice && <div className="notice identity-status-notice" role="status">
      <span aria-hidden="true">✓</span>
      <div>
        <b>{notice.message}</b>
        <small>{[notice.email, notice.userId, t(`identityUsers.status${notice.status.charAt(0).toUpperCase()}${notice.status.slice(1)}`, {defaultValue: notice.status})].filter(Boolean).join(" · ")}</small>
      </div>
    </div>}
    <div className="identity-users-toolbar">
      <label><span>{t("identityUsers.filterStatus")}</span><select value={statusFilter} onChange={event => onStatusFilterChange(event.target.value)} title={t("identityUsers.filterStatus")}>
        <option value="">{t("identityUsers.statusAll")}</option>
        <option value="active">{t("identityUsers.statusActive")}</option>
        <option value="inactive">{t("identityUsers.statusInactive")}</option>
        <option value="blocked">{t("identityUsers.statusBlocked")}</option>
        <option value="deleted">{t("identityUsers.statusDeleted")}</option>
      </select></label>
    </div>
    <DataTable rows={normalizedRows} datasetKey="identity:users:v4" defaultColumns={["_id", "email", "status", "datetimeVerified"]} detailsTitle={t("identityUsers.userDetails")} rowActions={actions} detailActions={actions} onRowAction={(_action, record) => openAction(record)} userTimeZone={userTimeZone} totalCount={totalCount} serverFilter={serverFilter}/>
    {pending && <div className="modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}><div ref={dialogRef} tabIndex={-1} className="modal confirm-modal identity-status-modal" role="alertdialog" aria-modal="true" aria-labelledby="identity-status-title">
      <button type="button" className="modal-close" title={t("administrators.close")} aria-label={t("administrators.close")} onClick={close} disabled={busy}>×</button>
      <div className={pending.blocked ? "danger-symbol" : "success-symbol"}>{pending.blocked ? "!" : "✓"}</div>
      <h2 id="identity-status-title">{t(pending.blocked ? "identityUsers.blockTitle" : "identityUsers.unblockTitle")}</h2>
      <p>{t(pending.blocked ? "identityUsers.blockConfirm" : "identityUsers.unblockConfirm")}</p>
      <div className="delete-target"><b>{pending.record.email || userIdOf(pending.record)}</b><small>{userIdOf(pending.record)}</small></div>
      <label className="identity-status-reason"><span>{t("identityUsers.reason")}</span><textarea autoFocus required maxLength={1000} value={reason} onChange={event => setReason(event.target.value)} placeholder={t("identityUsers.reasonPlaceholder")}/></label>
      {error && <div className="alert form-alert" role="alert">{error}</div>}
      <div className="modal-actions"><button type="button" className="secondary" title={t("administrators.cancel")} onClick={close} disabled={busy}>{t("administrators.cancel")}</button><button type="button" className={pending.blocked ? "danger-button" : "primary"} title={t("identityUsers.confirm")} onClick={submit} disabled={busy || !reason.trim()}>{busy ? t("identityUsers.processing") : t("identityUsers.confirm")}</button></div>
    </div></div>}
  </div>;
}
